import { z } from "zod";
import { acquisitionEstimateSchema } from "./acquisition-estimate.js";
import { acquisitionPublicationResultSchema, acquisitionQualificationSchema, publicationPricingInputSchema, } from "./acquisition-publication.js";
import { artifactRefSchema, workflowContextSchema } from "./context.js";
import { acquisitionPrincipalMoneySchema, monetaryBasisInputSchema, monetaryBasisResultSchema, preciseUnitMoneySchema, } from "./monetary-basis.js";
export const taxIssueCodeSchema = z.enum([
    "tax_config_table_not_configured",
    "ncm_missing_or_invalid",
    "origin_uf_missing_or_invalid",
    "destination_uf_missing_or_invalid",
    "fiscal_parameter_missing",
    "fiscal_parameter_ambiguous_for_ncm_route",
    "base_cost_missing_or_invalid",
    "base_cost_inferred_from_price",
    "original_mva_missing_or_invalid",
    "dest_internal_icms_rate_missing_or_invalid",
    "interstate_icms_rate_invalid",
    "ipi_rate_missing_or_invalid",
    "take_rate_missing_or_invalid",
    "operational_cost_invalid",
    "sale_leg_missing_or_invalid",
    "net_margin_unreachable",
    // Engine 1.7.0 (resale opt-in only).
    "resale_profile_pending",
    "icms_benefit_not_applicable",
    // Engine 1.8.0 (pisCofinsProfiles opt-in only).
    "pis_cofins_profile_pending",
    // Engine 1.11.0 (cbsIbs LC214_V1 opt-in): no published CBS/IBS rate for the year, or 2027+.
    "cbs_ibs_rate_pending",
    // Engine 1.11.0: the CBS/IBS classification needs the operator's confirmation.
    "cbs_ibs_class_pending",
    // Engine 1.15.0 (explicit supplier declaration).
    "purchase_st_status_pending",
    "purchase_credit_base_pending",
    "acquisition_qualification_pending",
]);
const moneySchema = z.string().regex(/^(0|[1-9]\d{0,15})(\.\d{1,2})?$/);
const rateSchema = z.string().regex(/^-?(0|[1-9]\d{0,5})(\.\d{1,8})?$/);
export const ufSchema = z.enum([
    "AC",
    "AL",
    "AP",
    "AM",
    "BA",
    "CE",
    "DF",
    "ES",
    "GO",
    "MA",
    "MT",
    "MS",
    "MG",
    "PA",
    "PB",
    "PR",
    "PE",
    "PI",
    "RJ",
    "RN",
    "RS",
    "RO",
    "RR",
    "SC",
    "SP",
    "SE",
    "TO",
]);
/**
 * Engine 1.1.0 opt-in: the PIS/COFINS regime the seller declares. Sending it
 * (UNKNOWN included) opts the item in to the 1.1.0 fiscal lines (FCP/FECP,
 * PIS/COFINS). UNKNOWN reports PIS/COFINS as not tracked ("não acompanhado"),
 * never as zero. Absent keeps the byte-identical 1.0.0 response.
 */
export const pisCofinsRegimeSchema = z.enum(["CUMULATIVE", "NON_CUMULATIVE", "UNKNOWN"]);
/**
 * Who buys from the BackChannel distribution center (the item's destination
 * UF is the center). Engine 1.5.0 `NET_TARGET_MARGIN` prices the SALE leg, so
 * the sale route and the customer type are part of the input.
 */
export const saleCustomerTypeSchema = z.enum(["ICMS_TAXPAYER", "FINAL_CONSUMER"]);
export const salePricingRouteSchema = z
    .object({
    /** Absent: an internal sale at the center (the item's destination UF), said so in the result. */
    destinationUf: ufSchema.optional(),
    customerType: saleCustomerTypeSchema,
})
    .strict();
/**
 * Engine 1.7.0, set by the platform (never by a caller: the API drops a client's value and resolves
 * it): the "regra geral (sem ST)" of a route that no published rule states. ICMS is the statutory
 * interstate rate (Res. SF 22/89) on an interstate route, or the destination's internal rate from
 * a CERTIFIED/VERIFIED fact on an internal one, with no ST because the NCM is proven outside ST on
 * the route: a CERTIFIED/VERIFIED fact (IOB "no ST"), or no CEST of the complete national catalog
 * (Conv. ICMS 142/2018) lists the NCM. It travels in the request, so it is hashed, stored with the
 * calculation and replayed exactly.
 */
export const icmsGeneralRuleSchema = z
    .object({
    kind: z.literal("NO_ST_GENERAL_RULE"),
    noStBasis: z.enum(["NO_ST_FACT", "OUTSIDE_CEST_CATALOG"]),
    noStFactId: z.string().uuid().optional(),
    interstateRate: rateSchema.nullable(),
    destinationInternalRate: rateSchema.nullable(),
    internalRateFactId: z.string().uuid().optional(),
    legalBasis: z.array(z.string().min(1).max(300)).min(1).max(8),
})
    .strict();
/** Engine 1.9.0: a priced sale leg was calculated with the regra geral (sem ST) of its route. */
export const saleGeneralRuleResultSchema = z
    .object({
    label: z.literal("regra geral (sem ST)"),
    noStBasis: icmsGeneralRuleSchema.shape.noStBasis,
    legalBasis: icmsGeneralRuleSchema.shape.legalBasis,
})
    .strict();
/**
 * Engine 1.9.0, set by the platform (never by a caller): the destination ICMS-ST rule of a route no
 * published rule states, derived from the published ST rule of the destination for the same NCM and
 * CEST ("derivada da regra de destino"). Only where the destination charges ST or antecipação on
 * entries from any origin (São Paulo: RICMS/SP art. 426-A and Portaria SRE 12/2026). The MVA, the
 * internal rate, the effective burden and FCP are the destination rule's, unchanged; the interstate
 * rate is the statutory one for the route (Res. SF 22/89; 4 % for imported goods through
 * `goodsOrigin`) and the policy derives the adjusted MVA. It names the published rule, so it is
 * rebuilt from the pinned ruleset and replayed exactly.
 */
export const icmsDestinationStRuleSchema = z
    .object({
    kind: z.literal("DESTINATION_ST_DERIVED"),
    /** The published rule the derived rule copies (an interstate rule of the destination first). */
    sourceRuleId: z.string().uuid(),
    /** Every published rule in force for the destination, NCM and CEST; all of them agreed. */
    agreeingRuleIds: z.array(z.string().uuid()).min(1).max(20),
    interstateRate: rateSchema,
    legalBasis: z.array(z.string().min(1).max(300)).min(1).max(8),
})
    .strict();
/** Engine 1.9.0: the purchase route used the destination's published ST rule for another origin. */
export const destinationStRuleResultSchema = z
    .object({
    label: z.string().regex(/^derivada da regra de destino \(origem [A-Z]{2}\)$/),
    sourceRuleId: z.string().uuid(),
    agreeingRuleIds: icmsDestinationStRuleSchema.shape.agreeingRuleIds,
    interstateRate: rateSchema,
    legalBasis: icmsDestinationStRuleSchema.shape.legalBasis,
})
    .strict();
const legalBasisSchema = z.array(z.string().min(1).max(300)).min(1).max(8);
/**
 * Engine 1.10.0, set by the platform (never by a caller; the API drops a client's value and sets it
 * while `FCP_IN_ALQ_INTRA_MODE=ON`): the destinations whose law puts the FECP/FCP inside ALQ_intra of
 * the adjusted MVA of an interstate ICMS-ST (Rio de Janeiro: 20 % + FECP 2 % = 22 %). It applies to
 * the purchase ST into such a destination and to a COST_PLUS_TR sale ST into it, only when the rule
 * of that leg states `fcpRate`. The ICMS-ST debit stays at the internal rate and the FECP-ST is its
 * own line (ST base × FECP rate). It travels in the request, so it is hashed and replayed exactly.
 */
export const icmsFcpInAlqIntraSchema = z
    .object({
    kind: z.literal("FCP_IN_ALQ_INTRA"),
    destinationUfs: z.array(ufSchema).min(1).max(27),
    legalBasis: legalBasisSchema,
})
    .strict();
/**
 * Engine 1.10.0, set by the platform (`SENDER_ST_WITHHOLDING_MODE=ON`): the purchase route is an
 * interstate ST protocol route on which the SENDER withholds the ICMS-ST on its invoice as
 * substitute (PR→SP foodstuffs, Protocolo ICMS 108/2013, cl. 1ª, for the CEST of Conv. ICMS 142/2018
 * Anexo XVII it does not except). The amounts are those of the route's ST rule; the result states
 * who withholds it instead of the destination's antecipação.
 */
export const icmsStSenderWithholdingSchema = z
    .object({
    kind: z.literal("SENDER_WITHHOLDING"),
    /** "Prot. ICMS 108/13". */
    agreement: z.string().min(1).max(80),
    originUf: ufSchema,
    destinationUf: ufSchema,
    legalBasis: legalBasisSchema,
})
    .strict();
/**
 * Engine 1.10.0, set by the platform (`PURCHASE_OWN_ICMS_STATUTORY_MODE=ON`) on items that ask for
 * the purchase lines: on an interstate purchase without ICMS-ST, the supplier's own ICMS is charged
 * at the statutory interstate rate of the route (Res. SF 22/89: 7 % or 12 %; Res. SF 13/2012: 4 % for
 * goods origin 1, 2, 3 and 8), not at a rate the rule leaves out or states otherwise.
 */
export const purchaseOwnIcmsRateSchema = z.literal("STATUTORY_INTERSTATE");
/** Engine 1.11.0 opt-in (ADR 0019): the CBS/IBS of the item's sale under catalog LC214_V1. */
export const cbsIbsCatalogSchema = z.literal("LC214_V1");
/**
 * Engine 1.11.0: the operator confirms the item's CBS/IBS class (cClassTrib of the national table,
 * six digits, the first three are the CST) and says why ("Anexo I item 16: pão francês"). The engine
 * accepts it only when a LC214_V1 catalog entry with that cClassTrib covers the NCM, or it is
 * "000001" (tributação integral); otherwise the item stays PENDING_CLASS.
 */
export const cbsIbsClassConfirmationSchema = z
    .object({ cClassTrib: z.string().regex(/^\d{6}$/), basis: z.string().trim().min(1).max(300) })
    .strict();
/** Engine 1.11.0: an NCM no LC214_V1 entry covers goes to review instead of tributação integral. */
export const cbsIbsUnmatchedSchema = z.literal("REVIEW");
/**
 * Engine 1.10.0: the destination's FECP inside ALQ_intra of the purchase ICMS-ST. ICMS-ST is
 * ST base × internal rate − own ICMS; FECP-ST is ST base × FECP on its own line; `stTotal` is both.
 */
export const fcpInAlqIntraResultSchema = z
    .object({
    destinationUf: ufSchema,
    internalRate: rateSchema,
    fcpRate: rateSchema,
    alqIntra: rateSchema,
    stBase: moneySchema,
    icmsSt: moneySchema,
    fcpSt: moneySchema,
    stTotal: moneySchema,
    legalBasis: legalBasisSchema,
})
    .strict();
/**
 * Engine 1.10.0: who collects the purchase ICMS-ST. SENDER: the supplier withholds it on its
 * invoice as substitute under the agreement named.
 */
export const stCollectionResultSchema = z
    .object({
    responsible: z.literal("SENDER"),
    label: z.literal("ST retido pelo remetente"),
    agreement: icmsStSenderWithholdingSchema.shape.agreement,
    originUf: ufSchema,
    destinationUf: ufSchema,
    amount: moneySchema,
    fcpAmount: moneySchema,
    legalBasis: legalBasisSchema,
})
    .strict();
/**
 * Engine 1.10.0: the statutory interstate rate used as the purchase own ICMS when
 * `purchaseOwnIcmsRate` is STATUTORY_INTERSTATE.
 */
export const statutoryOwnIcmsRateResultSchema = z
    .object({
    rate: rateSchema,
    ruleRate: rateSchema.nullable(),
    basis: z.enum(["DOMESTIC", "IMPORTED", "RULE_IMPORTED"]),
    legalReference: z.string().min(1).max(500),
})
    .strict();
/**
 * Engine 1.1.0 (optional, opt-in): how to price the sale from the landed unit
 * cost. MARKUP: price = cost × (1 + rate). TARGET_MARGIN (gross margin on the
 * price, Onil v15 default): price = cost ÷ (1 − rate). Rates are fractions.
 *
 * Engine 1.5.0 NET_TARGET_MARGIN: `rate` is the NET margin the sale must leave
 * after the taxes of the sale and the credits of the purchase:
 * price = (cost − credits) ÷ (1 − rate − Σ sale taxes that are a share of the
 * price). `sale` states who buys and where; absent, the sale is an internal
 * sale at the center to an ICMS taxpayer, and the result says both were
 * assumed.
 *
 * Engine 1.6.0 COST_PLUS_TR ("Valor de venda com TR", the operators' workbook):
 * price = purchase invoice cost ÷ (1 − TR), the invoice cost being base + IPI +
 * ICMS-ST (+ FCP-ST); the sale's own ICMS, PIS/COFINS (outside the ICMS base),
 * ICMS-ST/FCP-ST and the buyer's invoice follow the sale route, as in the
 * workbook's columns AR..AV. `sale` states who buys and where, as above.
 */
export const pricingInputSchema = z
    .object({
    method: z.enum(["MARKUP", "TARGET_MARGIN", "NET_TARGET_MARGIN", "COST_PLUS_TR"]),
    rate: z.string().regex(/^(0|[1-9]\d{0,3})(\.\d{1,8})?$/),
    sale: salePricingRouteSchema.optional(),
    /**
     * Engine 1.9.0, set by the platform like the item's `icmsGeneralRule`: the regra geral (sem ST)
     * of the sale route of NET_TARGET_MARGIN or COST_PLUS_TR when no published rule states it.
     */
    icmsGeneralRule: icmsGeneralRuleSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    const rate = Number(value.rate);
    if (value.method !== "MARKUP" && !(rate < 1)) {
        context.addIssue({
            code: "custom",
            path: ["rate"],
            message: value.method === "COST_PLUS_TR"
                ? "A TR must be below 1 (100%)"
                : "A target margin must be below 1 (100%)",
        });
    }
    if (value.method === "MARKUP" && rate > 100) {
        context.addIssue({ code: "custom", path: ["rate"], message: "Markup must not exceed 100" });
    }
    if (value.sale !== undefined &&
        value.method !== "NET_TARGET_MARGIN" &&
        value.method !== "COST_PLUS_TR") {
        context.addIssue({
            code: "custom",
            path: ["sale"],
            message: "The sale route only applies to NET_TARGET_MARGIN and COST_PLUS_TR",
        });
    }
    if (value.icmsGeneralRule !== undefined &&
        value.method !== "NET_TARGET_MARGIN" &&
        value.method !== "COST_PLUS_TR") {
        context.addIssue({
            code: "custom",
            path: ["icmsGeneralRule"],
            message: "Only a pricing with a sale leg (NET_TARGET_MARGIN, COST_PLUS_TR) has a sale rule",
        });
    }
});
/**
 * Engine 1.6.0 item opt-in, like `pisCofinsRegime` and `pricing`. DETAILED: the
 * item carries the purchase taxes the operators' workbooks show, even with no
 * pricing: the supplier's own ICMS (at the internal effective burden the rule
 * states on an internal route, e.g. RICMS/SP Anexo II art. 39) and the
 * purchase PIS/COFINS outside the ICMS base, `(base − own ICMS) × rate`,
 * reported as a credit or as information and never added to the landed cost.
 * On an internal ICMS-ST route the ST credits that same own ICMS. Absent keeps
 * the earlier behaviour, stamp and digest byte for byte.
 */
export const purchaseTaxesDetailSchema = z.literal("DETAILED");
/**
 * Engine 1.7.0 item opt-in: the goods origin of the batch as the invoice states it (NF-e `orig`,
 * 0–8). On an interstate operation origins 1, 2, 3 and 8 (imported, or import content above 40 %)
 * take the 4 % rate of Res. SF 13/2012, whatever the route rule states for domestic goods; 0, 4, 5,
 * 6 and 7 keep the rule's rate. Absent: the rule's rate, as before. A master-data origin does not
 * prove the batch's origin: it is the operator's statement for this lot.
 */
export const goodsOriginSchema = z.enum(["0", "1", "2", "3", "4", "5", "6", "7", "8"]);
/**
 * Engine 1.7.0: how PIS/COFINS apply to the resale, stated by the operator. STANDARD: the regime
 * of the seller (non-cumulative credits on the purchase, debits on the sale). SINGLE_STAGE_RESALE:
 * goods of Lei 10.147/2000 (monofásico) resold by a company that is neither manufacturer nor
 * importer: zero rate on the sale (art. 2º) and no credit on the purchase (Lei 10.637/2002 and
 * 10.833/2003, art. 3º, I, "b"). ZERO_RATE: a zero-rated product (e.g. Lei 10.925/2004, art. 1º)
 * whose classification the operator confirmed: zero on the sale, no credit on the purchase.
 */
export const resalePisCofinsTreatmentSchema = z.enum([
    "STANDARD",
    "SINGLE_STAGE_RESALE",
    "ZERO_RATE",
]);
/**
 * Engine 1.8.0 item opt-in: resolve the item's PIS/COFINS profile from the statutory catalog the
 * value names, and apply it in every path (MARKUP, TARGET_MARGIN, NET_TARGET_MARGIN, COST_PLUS_TR,
 * the purchase lines and the resale). STATUTORY_V1 is the catalog of engine 1.8.0: monofásico
 * resale (Lei 10.147/2000, Lei 10.485/2002) is CST 04 with no debit on the sale and no credit on
 * the purchase; zero rate (Lei 10.925/2004 art. 1º, Lei 10.865/2004 art. 28) is CST 06 with no
 * debit and no credit; cold beverages (Lei 13.097/2015) and any entry whose legal condition the
 * NCM alone cannot prove stay PENDING and go to review instead of a silent zero. Every other NCM
 * keeps the regime's rates. Absent keeps the earlier behaviour, stamp and digest byte for byte;
 * a new catalog revision is a new value and a new engine version. STATUTORY_V2 (engine 1.13.0) is
 * STATUTORY_V1 unchanged plus the monofásico fuels (Lei 9.718/1998 arts. 4º-5º, Lei 10.560/2002,
 * Lei 11.116/2005), each to confirm but jet fuel, and the zero rate of natural mineral water (Lei
 * 12.715/2012 art. 76), which puts 2201.10.00 on two lists with the beverages of Lei 13.097.
 */
export const pisCofinsProfilesSchema = z.enum(["STATUTORY_V1", "STATUTORY_V2"]);
/**
 * A conditional ICMS relief (cBenef) the operator affirms for this sale, e.g. SP020340 (RICMS/SP
 * Anexo II art. 34). The engine applies it only when the published sale rule states that benefit
 * and it is in force; the affirmation is the operator's, recorded with the calculation.
 */
export const icmsBenefitClaimSchema = z
    .object({
    code: z.string().regex(/^[A-Z]{2}\d{6}$/),
    eligibility: z.literal("AFFIRMED"),
})
    .strict();
/**
 * Engine 1.7.0 resale at a known price (the package's "compra e venda"): purchase A→B at the base
 * cost, which already includes the supplier's own ICMS, then sale B→C at `salePrice`, in the same
 * quantity basis as the base cost (a line total when the cost is a line total). The result is the
 * contribution and the margin on revenue, or a stated pending reason, never a silent zero.
 */
export const resaleInputSchema = z
    .object({
    salePrice: preciseUnitMoneySchema,
    sale: salePricingRouteSchema.optional(),
    pisCofinsTreatment: resalePisCofinsTreatmentSchema.optional(),
    icmsBenefit: icmsBenefitClaimSchema.optional(),
    /**
     * After entry ICMS-ST on an internal resale (CST 60), PIS/COFINS of the sale are charged on the
     * sale price: the operator states it when its tax policy is documented. Absent: the margin is
     * PENDING (`ST_RESALE_PIS_COFINS_UNDEFINED`), the purchase and the ST are still calculated.
     */
    stResalePisCofins: z.literal("ON_SALE_PRICE").optional(),
    /** Set by the platform, like the item's `icmsGeneralRule`: the regra geral of the sale route. */
    icmsGeneralRule: icmsGeneralRuleSchema.optional(),
})
    .strict();
export const baseCostCandidatesSchema = z
    .object({
    finalCost: moneySchema.optional(),
    originalCost: moneySchema.optional(),
    currentSalePrice: moneySchema.optional(),
    suggestedSalePrice: moneySchema.optional(),
    originalSalePrice: moneySchema.optional(),
})
    .strict();
/** Ordinary distributor excludes industrial-equivalent establishments. */
export const supplierTypeSchema = z.enum(["INDUSTRY", "DISTRIBUTOR"]);
export const calculationItemInputSchema = z
    .object({
    itemId: z.string().min(1).max(100),
    position: z.number().int().nonnegative(),
    sku: z.string().max(200).optional(),
    category: z.string().max(200).optional(),
    ncm: z
        .string()
        .regex(/^\d{8}$/)
        .optional(),
    cest: z
        .string()
        .regex(/^\d{2}\.\d{3}\.\d{2}$/)
        .optional(),
    originUf: ufSchema.optional(),
    destinationUf: ufSchema.optional(),
    paymentMethod: z.string().max(100).optional(),
    transactionType: z.string().max(100).optional(),
    quantity: z.number().int().positive().optional(),
    monetaryBasis: monetaryBasisInputSchema.optional(),
    /** Opt-in supplier declaration; absent preserves historical published-rule behavior. */
    supplierType: supplierTypeSchema.optional(),
    /** Prior ST included in the goods price: ordinary distributor, internal route, currently applicable ST only. */
    purchaseStPreviouslyPaid: z.boolean().optional(),
    baseCostCandidates: baseCostCandidatesSchema.extend({
        finalCost: acquisitionPrincipalMoneySchema.optional(),
        originalCost: acquisitionPrincipalMoneySchema.optional(),
        currentSalePrice: preciseUnitMoneySchema.optional(),
        suggestedSalePrice: preciseUnitMoneySchema.optional(),
        originalSalePrice: preciseUnitMoneySchema.optional(),
    }),
    pisCofinsRegime: pisCofinsRegimeSchema.optional(),
    pricing: pricingInputSchema.optional(),
    publicationPricing: publicationPricingInputSchema.optional(),
    acquisitionQualification: acquisitionQualificationSchema.optional(),
    purchaseTaxes: purchaseTaxesDetailSchema.optional(),
    goodsOrigin: goodsOriginSchema.optional(),
    /**
     * Engine 1.12.0: the item's goodsOrigin also sets the 4 % interstate rate on the sale leg of
     * COST_PLUS_TR / NET_TARGET_MARGIN. Requires `goodsOrigin`. Absent keeps the earlier behaviour,
     * stamp and digest byte for byte.
     */
    saleGoodsOrigin: z.literal("APPLY").optional(),
    resale: resaleInputSchema.optional(),
    icmsGeneralRule: icmsGeneralRuleSchema.optional(),
    /** Engine 1.8.0 opt-in: the statutory PIS/COFINS profile catalog (see the schema). */
    pisCofinsProfiles: pisCofinsProfilesSchema.optional(),
    /**
     * Engine 1.8.0: the treatment the operator confirms for the item, e.g. ZERO_RATE for an entry
     * whose condition (an "Ex" of the TIPI, a narrower description) the NCM cannot prove. A
     * treatment the catalog does not back for the NCM stays PENDING. Requires `pisCofinsProfiles`.
     */
    pisCofinsTreatment: resalePisCofinsTreatmentSchema.optional(),
    icmsDestinationStRule: icmsDestinationStRuleSchema.optional(),
    /** Engine 1.10.0, set by the platform: FECP inside ALQ_intra (see the schema). */
    icmsFcpInAlqIntra: icmsFcpInAlqIntraSchema.optional(),
    /** Engine 1.10.0, set by the platform: the sender withholds the ST (see the schema). */
    icmsStSenderWithholding: icmsStSenderWithholdingSchema.optional(),
    /** Engine 1.10.0, set by the platform: statutory interstate own ICMS (see the schema). */
    purchaseOwnIcmsRate: purchaseOwnIcmsRateSchema.optional(),
    /** Engine 1.11.0 opt-in (ADR 0019): the CBS/IBS of the item's sale under catalog LC214_V1. */
    cbsIbs: cbsIbsCatalogSchema.optional(),
    /** Engine 1.11.0: the operator's confirmation of the CBS/IBS classification (see the schema). */
    cbsIbsClass: cbsIbsClassConfirmationSchema.optional(),
    /** Engine 1.11.0, set by the platform (CBS_IBS_UNMATCHED=REVIEW; the API drops a caller's value). */
    cbsIbsUnmatched: cbsIbsUnmatchedSchema.optional(),
})
    .strict()
    .superRefine((item, context) => {
    if (item.purchaseStPreviouslyPaid !== undefined &&
        (item.supplierType === undefined ||
            (item.purchaseStPreviouslyPaid && item.supplierType !== "DISTRIBUTOR"))) {
        context.addIssue({
            code: "custom",
            path: ["purchaseStPreviouslyPaid"],
            message: "Prior-ST declaration requires an ordinary DISTRIBUTOR supplier",
        });
    }
    if (item.purchaseStPreviouslyPaid && (!item.originUf || item.originUf !== item.destinationUf)) {
        context.addIssue({
            code: "custom",
            path: ["purchaseStPreviouslyPaid"],
            message: "Prior ST included in price is supported only on an explicit internal route; interstate withholding requires invoice analysis",
        });
    }
    const unit = item.monetaryBasis?.type === "UNIT";
    if (item.monetaryBasis &&
        (!Number.isSafeInteger(item.quantity ?? 1) ||
            (item.quantity ?? 1) > 999999999 ||
            (unit && item.quantity === undefined))) {
        context.addIssue({
            code: "custom",
            path: ["quantity"],
            message: "Explicit unit values require a quantity from 1 to 999999999",
        });
    }
    for (const [field, value] of [
        ...Object.entries(item.baseCostCandidates),
        ...(item.resale ? [["resale.salePrice", item.resale.salePrice]] : []),
    ]) {
        if (value === undefined)
            continue;
        const exactPrincipal = unit &&
            item.publicationPricing !== undefined &&
            (field === "finalCost" || field === "originalCost");
        if (!preciseUnitMoneySchema.safeParse(value).success && !exactPrincipal) {
            context.addIssue({
                code: "custom",
                path: ["baseCostCandidates", field ?? ""],
                message: "More than six fractional digits require explicit acquisition publication pricing",
            });
        }
        if (exactPrincipal &&
            acquisitionPrincipalMoneySchema.safeParse(value).success &&
            !preciseUnitMoneySchema.safeParse(value).success &&
            Number.isSafeInteger(item.quantity)) {
            const [whole = "0", fraction = ""] = value.split(".");
            if (BigInt(whole + fraction) * BigInt(item.quantity ?? 1) * 1000n >=
                (10n ** 19n - 5n) * 10n ** BigInt(fraction.length)) {
                context.addIssue({
                    code: "custom",
                    path: ["baseCostCandidates", field ?? ""],
                    message: "Rounded line total exceeds the monetary limit",
                });
            }
        }
        if (!unit && !moneySchema.safeParse(value).success) {
            context.addIssue({
                code: "custom",
                path: field === "resale.salePrice"
                    ? ["resale", "salePrice"]
                    : ["baseCostCandidates", field ?? ""],
                message: "Sub-cent values require explicit UNIT monetaryBasis",
            });
        }
        if (unit &&
            preciseUnitMoneySchema.safeParse(value).success &&
            Number.isSafeInteger(item.quantity) &&
            (item.quantity ?? 0) > 0) {
            const [whole = "0", fraction = ""] = value.split(".");
            if (BigInt(whole + fraction.padEnd(6, "0")) * BigInt(item.quantity ?? 1) >=
                10n ** 22n - 5000n)
                context.addIssue({
                    code: "custom",
                    path: field === "resale.salePrice"
                        ? ["resale", "salePrice"]
                        : ["baseCostCandidates", field ?? ""],
                    message: "Rounded line total exceeds the monetary limit",
                });
        }
    }
    if (item.pisCofinsTreatment !== undefined && item.pisCofinsProfiles === undefined) {
        context.addIssue({
            code: "custom",
            path: ["pisCofinsTreatment"],
            message: "pisCofinsTreatment needs pisCofinsProfiles",
        });
    }
    if (item.saleGoodsOrigin !== undefined && item.goodsOrigin === undefined) {
        context.addIssue({
            code: "custom",
            path: ["saleGoodsOrigin"],
            message: "saleGoodsOrigin needs goodsOrigin",
        });
    }
    if (item.cbsIbsClass !== undefined && item.cbsIbs === undefined) {
        context.addIssue({
            code: "custom",
            path: ["cbsIbsClass"],
            message: "requires cbsIbs",
        });
    }
    if (item.cbsIbsUnmatched !== undefined && item.cbsIbs === undefined) {
        context.addIssue({
            code: "custom",
            path: ["cbsIbsUnmatched"],
            message: "requires cbsIbs",
        });
    }
    const resaleTreatment = item.resale?.pisCofinsTreatment;
    if (item.pisCofinsTreatment !== undefined &&
        resaleTreatment !== undefined &&
        resaleTreatment !== item.pisCofinsTreatment) {
        context.addIssue({
            code: "custom",
            path: ["pisCofinsTreatment"],
            message: "pisCofinsTreatment and resale.pisCofinsTreatment must agree",
        });
    }
});
/**
 * Engine 1.2.0 request scope. FISCAL computes the fiscal result (IPI, ICMS,
 * ICMS-ST and the fiscal landed cost) whenever the fiscal inputs and rules are
 * complete, and reports the operational cost and the commercial fee as
 * separate, non-blocking components: with no rule they read NOT_CONFIGURED and
 * stay out of every total. Absent (or FULL) keeps the earlier behaviour, where
 * a missing operational cost or take rate sends the item to review.
 */
export const costScopeSchema = z.enum(["FISCAL", "FULL"]);
/**
 * Engine 1.4.0 request flag. REQUIRED: a route whose only fiscal rule is an
 * IPI (TIPI) row, with ICMS EXCLUDED, is not silently calculated with ICMS 0;
 * the item gets the blocking `fiscal_parameter_missing` issue (IPI still comes
 * from the TIPI row). Absent keeps the earlier behaviour so stored requests
 * replay byte for byte.
 */
export const icmsRuleRequirementSchema = z.literal("REQUIRED");
export const rulesetSelectorSchema = z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("ACTIVE") }).strict(),
    z
        .object({
        mode: z.literal("VERSION"),
        version: z.string().min(1).max(80),
    })
        .strict(),
]);
export const batchCalculationRequestSchema = workflowContextSchema
    .extend({
    schemaVersion: z.literal("1.0.0"),
    country: z.literal("BR").default("BR"),
    currency: z.literal("BRL").default("BRL"),
    calculationDate: z.string().date(),
    rulesetSelector: rulesetSelectorSchema,
    costScope: costScopeSchema.optional(),
    icmsRule: icmsRuleRequirementSchema.optional(),
    batch: z
        .object({
        index: z.number().int().nonnegative(),
        count: z.number().int().positive(),
    })
        .strict(),
    items: z.array(calculationItemInputSchema).min(1).max(500),
})
    .strict()
    .superRefine((request, context) => {
    if (request.batch.index >= request.batch.count) {
        context.addIssue({
            code: "custom",
            message: "batch.index must be less than batch.count",
            path: ["batch", "index"],
        });
    }
    if (request.items.some((item) => item.monetaryBasis !== undefined || item.supplierType !== undefined) &&
        request.costScope !== "FISCAL") {
        context.addIssue({
            code: "custom",
            path: ["costScope"],
            message: "Explicit monetary basis and supplier profiles are supported only for FISCAL calculations",
        });
    }
    const ids = new Set();
    const positions = new Set();
    for (const item of request.items) {
        if (ids.has(item.itemId)) {
            context.addIssue({ code: "custom", message: "itemId must be unique", path: ["items"] });
        }
        if (positions.has(item.position)) {
            context.addIssue({ code: "custom", message: "position must be unique", path: ["items"] });
        }
        ids.add(item.itemId);
        positions.add(item.position);
        if (item.resale !== undefined && item.pricing !== undefined) {
            context.addIssue({
                code: "custom",
                message: "An item is priced (pricing) or resold at a known price (resale), not both",
                path: ["items"],
            });
        }
    }
});
/** Safe original inputs for a new calculation; never a replayable workflow envelope. */
export const calculationSourceInputSchema = z
    .object({
    calculationDate: batchCalculationRequestSchema.shape.calculationDate,
    items: batchCalculationRequestSchema.shape.items,
})
    .strict()
    .superRefine((input, context) => {
    for (const field of ["itemId", "position"]) {
        if (new Set(input.items.map((item) => item[field])).size !== input.items.length) {
            context.addIssue({ code: "custom", path: ["items"], message: `${field} must be unique` });
        }
    }
});
export const calculationProvenanceSchema = z
    .object({
    ruleId: z.string().uuid(),
    evidenceId: z.string().uuid(),
    contentSha256: z.string().regex(/^[a-f0-9]{64}$/),
    source: z.string().min(1),
    sourceUrl: z.string().url().optional(),
    legalReference: z.string().optional(),
    validFrom: z.string().date().nullable(),
    validTo: z.string().date().nullable(),
})
    .strict();
const signedMoneySchema = z.string().regex(/^-?(0|[1-9]\d{0,15})(\.\d{1,2})?$/);
/** FCP/FECP (fundo de combate à pobreza) stated by the destination rule. */
export const fcpLineSchema = z
    .object({
    /** NOT_TRACKED: the rule in force does not state FCP (shown as "não acompanhado"). */
    status: z.enum(["CALCULATED", "NOT_APPLICABLE", "NOT_TRACKED", "EXEMPT"]),
    mode: z.enum(["ST", "STANDARD"]).nullable(),
    rate: rateSchema.nullable(),
    base: moneySchema.nullable(),
    amount: moneySchema.nullable(),
})
    .strict();
/** PIS or COFINS rate on the sale: from the rule, the declared regime, or not tracked. */
export const contributionLineSchema = z
    .object({
    status: z.enum(["TRACKED", "EXEMPT", "NOT_TRACKED"]),
    /** PIS_COFINS_PROFILE (engine 1.8.0): a monofásico or zero-rate profile sets the rate to zero. */
    source: z.enum(["RULE", "REGIME_DEFAULT", "PIS_COFINS_PROFILE"]).nullable(),
    regime: pisCofinsRegimeSchema.nullable(),
    rate: rateSchema.nullable(),
    legalReference: z.string().nullable(),
})
    .strict();
/** Engine 1.1.0 fiscal lines; present only when the item opted in or FCP applies. */
export const fiscalLinesSchema = z
    .object({
    fcp: fcpLineSchema,
    pis: contributionLineSchema,
    cofins: contributionLineSchema,
})
    .strict();
/** Engine 1.1.0 sale price and margin; present only when the item asked for pricing. */
export const netPricingSchema = z
    .object({
    /** The sale leg the price was solved for, and which parts of it the request did not state. */
    saleRoute: z
        .object({
        originUf: ufSchema,
        destinationUf: ufSchema,
        customerType: saleCustomerTypeSchema,
        /** True when the destination was not stated: an internal sale at the center. */
        destinationAssumed: z.boolean(),
        /** True when the customer type was not stated: an ICMS taxpayer. */
        customerTypeAssumed: z.boolean(),
    })
        .strict(),
    /** TAXED: own ICMS of the sale; ST_CLOSED_ON_ENTRY: entry ICMS-ST closed the chain; NOT_TAXED: exempt or not taxed. */
    icmsStatus: z.enum(["TAXED", "ST_CLOSED_ON_ENTRY", "NOT_TAXED"]),
    icmsRate: rateSchema,
    icmsOnSale: moneySchema,
    /** False when the sale rule does not state an FCP rate: it is not tracked, not zero. */
    fcpTracked: z.boolean(),
    fcpRate: rateSchema,
    fcpOnSale: moneySchema,
    difalRate: rateSchema,
    difalOnSale: moneySchema,
    /** Purchase credits that lower the cost the price must recover. */
    credits: z
        .object({
        icms: moneySchema,
        pis: moneySchema,
        cofins: moneySchema,
        total: moneySchema,
    })
        .strict(),
    /** Landed cost minus credits. */
    netCost: moneySchema,
    /** 1 − target margin − Σ taxes that are a share of the price. */
    divisor: rateSchema,
    /** Sale price ÷ landed cost − 1. */
    markupPct: rateSchema,
    /** Engine 1.9.0: the sale route had no published rule; the regra geral answered it. */
    saleGeneralRule: saleGeneralRuleResultSchema.optional(),
})
    .strict();
/**
 * Engine 1.6.0 COST_PLUS_TR: the sale leg of "Valor de venda com TR" (workbook columns AR..AV and
 * AX..BH). Every rate comes from the published rule of the sale route; a rate it does not state is
 * not tracked. The price is not grossed up for the sale taxes: as in the workbook, the margin left
 * after them is lower than the TR, and the result says so.
 */
export const trPricingSchema = z
    .object({
    /** PURCHASE_INVOICE: base + IPI + ICMS-ST (+ FCP-ST); the supplier's own ICMS is in the base. */
    costBasisKind: z.literal("PURCHASE_INVOICE"),
    saleRoute: netPricingSchema.shape.saleRoute,
    /** ST_CLOSED_ON_ENTRY: entry ICMS-ST closed an internal sale (CST 60); OWN_ICMS; NOT_TAXED. */
    icmsTreatment: z.enum(["ST_CLOSED_ON_ENTRY", "OWN_ICMS", "NOT_TAXED"]),
    cst: z.enum(["00", "10", "20", "40", "60"]).nullable(),
    icmsRate: rateSchema,
    /** RULE_BURDEN: an internal sale at the effective burden the rule states. */
    icmsRateSource: z.enum(["RULE_BURDEN", "NOMINAL", "NOMINAL_NOT_INFORMED", "NONE"]),
    icmsOnSale: moneySchema,
    /** Sale price minus the sale's own ICMS (STF Tema 69). */
    pisCofinsBase: moneySchema,
    st: z
        .object({
        /** ON_INVOICE: BackChannel retains it as substitute; NOT_ON_INVOICE: an ST rule the invoice does not carry. */
        treatment: z.enum(["ON_INVOICE", "NOT_ON_INVOICE", "NOT_APPLICABLE"]),
        originalMva: rateSchema,
        adjustedMva: rateSchema,
        internalRate: rateSchema,
        base: moneySchema,
        amount: moneySchema,
        fcpTracked: z.boolean(),
        fcpRate: rateSchema.nullable(),
        fcpAmount: moneySchema,
        /**
         * Engine 1.10.0 (`icmsFcpInAlqIntra`): ALQ_intra of the adjusted MVA with the destination's
         * FECP inside (RJ 20 % + 2 % = 22 %), and its legal basis. Absent: ALQ_intra without FECP.
         */
        alqIntra: rateSchema.optional(),
        fcpInAlqIntraBasis: legalBasisSchema.optional(),
    })
        .strict(),
    difal: z.object({ rate: rateSchema, amount: moneySchema, fcpAmount: moneySchema }).strict(),
    /** What the buyer's invoice totals: price + ICMS-ST + FCP-ST. */
    buyerInvoice: moneySchema,
    /** Engine 1.9.0: the sale route had no published rule; the regra geral answered it. */
    saleGeneralRule: saleGeneralRuleResultSchema.optional(),
})
    .strict();
/**
 * Engine 1.7.0 resale at a known price. PENDING says why the margin has no value: entry ST closed
 * the chain and the PIS/COFINS treatment of the ST resale is not stated, or the product needs a
 * PIS/COFINS profile the operator has not given (beverages of Lei 13.097/2015, zero-rated foods
 * such as coffee, single-stage goods of Lei 10.147/2000). Null means pending, never zero.
 */
export const resalePendingReasonSchema = z.enum([
    "ST_RESALE_PIS_COFINS_UNDEFINED",
    "BEVERAGE_PROFILE",
    "ZERO_RATE_CLASSIFICATION",
    "SINGLE_STAGE_TREATMENT",
    /** No rule states PIS/COFINS and the regime is UNKNOWN: credits and debits are not tracked. */
    "PIS_COFINS_REGIME_UNKNOWN",
    /** Prior ST included in a distributor price without a qualified credit-base breakdown. */
    "PURCHASE_CREDIT_BASE_UNKNOWN",
    /** The sale route has no usable rule (`sale_leg_missing_or_invalid` says why). */
    "SALE_RULE_MISSING",
]);
export const resaleResultSchema = z
    .object({
    status: z.enum(["CALCULATED", "PENDING"]),
    pendingReason: resalePendingReasonSchema.nullable(),
    pisCofinsTreatment: resalePisCofinsTreatmentSchema.nullable(),
    goodsOrigin: goodsOriginSchema.nullable(),
    purchase: z
        .object({
        /** The base cost: merchandise value before IPI and ST, the supplier's own ICMS included. */
        merchandise: moneySchema,
        /** Null while the product's profile is pending. */
        ownIcmsRate: rateSchema.nullable(),
        ownIcms: moneySchema.nullable(),
        ipi: moneySchema,
        stApplied: z.boolean(),
        adjustedMva: rateSchema,
        stBase: moneySchema,
        stValue: moneySchema,
        /** Paid to the supplier and to the state: merchandise + IPI + ICMS-ST. */
        cash: moneySchema,
        icmsCredit: moneySchema.nullable(),
        /** Merchandise minus the purchase own ICMS (Lei 10.637/2002 art. 3º §2º III). */
        pisCofinsCreditBase: moneySchema.nullable(),
        pisCredit: moneySchema.nullable(),
        cofinsCredit: moneySchema.nullable(),
        /** Cash minus the credits the scenario can use. */
        netCost: moneySchema.nullable(),
    })
        .strict(),
    sale: z
        .object({
        route: netPricingSchema.shape.saleRoute,
        revenue: moneySchema,
        icmsTreatment: z.enum(["OWN_ICMS", "ST_CLOSED_ON_ENTRY", "NOT_TAXED"]),
        cst: z.enum(["00", "20", "40", "60"]).nullable(),
        icmsNominalRate: rateSchema,
        /** Effective burden on the revenue: nominal × (1 − reduction). */
        icmsRate: rateSchema,
        baseReductionPct: rateSchema,
        icmsBase: moneySchema,
        icms: moneySchema,
        benefit: z
            .object({
            code: z.string().min(1).max(20),
            status: z.enum(["APPLIED", "NOT_IN_RULE"]),
            legalReference: z.string().max(500).nullable(),
        })
            .strict()
            .nullable(),
        /** Revenue minus the sale's own ICMS (STF Tema 69). */
        pisCofinsBase: moneySchema,
        pisRate: rateSchema,
        cofinsRate: rateSchema,
        pis: moneySchema,
        cofins: moneySchema,
        pisCofinsCst: z.enum(["01", "04", "06"]),
        difal: moneySchema,
        fcp: moneySchema,
    })
        .strict()
        .nullable(),
    /** Revenue − sale taxes − net cost; null while pending. */
    contribution: signedMoneySchema.nullable(),
    /** Contribution ÷ revenue (0.30 = 30 %); null while pending. */
    marginRatio: rateSchema.nullable(),
    legalBasis: z.array(z.string().min(1).max(300)).max(16),
})
    .strict();
export const pricingResultSchema = z
    .object({
    method: z.enum(["MARKUP", "TARGET_MARGIN", "NET_TARGET_MARGIN", "COST_PLUS_TR"]),
    rate: rateSchema,
    costBasis: moneySchema,
    /**
     * Engine 1.2.0: FISCAL when the cost basis is the fiscal landed cost because the operational
     * cost or commercial fee is not configured, so the margin is before those costs; COMMERCIAL
     * when they are in it. Absent before 1.2.0.
     */
    costBasisScope: z.enum(["FISCAL", "COMMERCIAL"]).optional(),
    salePrice: moneySchema,
    /** Null when PIS/COFINS is not tracked for this item. */
    pisOnSale: moneySchema.nullable(),
    cofinsOnSale: moneySchema.nullable(),
    marginValue: signedMoneySchema,
    marginPct: rateSchema,
    pisCofinsTracked: z.boolean(),
    /** Engine 1.5.0 NET_TARGET_MARGIN only: the sale taxes, credits and formula terms. */
    net: netPricingSchema.optional(),
    /** Engine 1.6.0 COST_PLUS_TR only: the sale leg of "Valor de venda com TR". */
    tr: trPricingSchema.optional(),
})
    .strict();
/**
 * Engine 1.6.0 (purchaseTaxes DETAILED): the ICMS the supplier charges on its
 * own operation, as the purchase invoice shows it. On an internal route the
 * rate is the effective internal burden the rule states (a base reduction such
 * as RICMS/SP Anexo II art. 39: 18% × 66,67% = 12%); without one it is the
 * nominal rate and `burdenSource` says the reduction was not stated.
 */
export const purchaseOwnIcmsSchema = z
    .object({
    /** CALCULATED; NOT_TAXED: exempt or no own ICMS highlighted on a confirmed substituted-reseller invoice; NOT_TRACKED: no route rule. */
    status: z.enum(["CALCULATED", "NOT_TAXED", "NOT_TRACKED"]),
    route: z.enum(["INTRASTATE", "INTERSTATE"]),
    /** The supplier price the own ICMS is charged on (the selected base cost). */
    base: moneySchema,
    /** Nominal rate of the route: internal on an internal route, interstate otherwise. */
    nominalRate: rateSchema.nullable(),
    /** Rate applied: the stated effective burden on an internal route, else the nominal rate. */
    rate: rateSchema.nullable(),
    /** Base reduction implied by the burden, in percentage points (33.33333333 for 12% of 18%). */
    baseReductionPct: rateSchema.nullable(),
    /** Reduced ICMS base: base × rate ÷ nominal rate ("Base de cálculo ICMS"). */
    reducedBase: moneySchema.nullable(),
    amount: moneySchema.nullable(),
    /**
     * RULE: the rule states the effective burden in force. NOMINAL_NOT_INFORMED: an internal route
     * whose rule states none, so any base reduction must be published as a rule.
     * NOMINAL_OUT_OF_VALIDITY: the stated burden is outside its validity. NOT_APPLICABLE: an
     * interstate route or no taxed ICMS.
     */
    burdenSource: z.enum([
        "RULE",
        "NOMINAL_NOT_INFORMED",
        "NOMINAL_OUT_OF_VALIDITY",
        "NOT_APPLICABLE",
        /** Engine 1.10.0: an interstate purchase without ST at the Res. SF 22/89 (or 13/2012) rate. */
        "STATUTORY_INTERSTATE",
    ]),
    legalReference: z.string().min(1).max(500).nullable(),
})
    .strict();
/**
 * Engine 1.6.0: PIS/COFINS of the purchase outside the ICMS base,
 * `(base − own ICMS) × rate` (STF Tema 69). CREDIT under the non-cumulative
 * regime; INFORMATIONAL otherwise. Never part of the landed cost.
 */
export const purchasePisCofinsSchema = z
    .object({
    status: z.enum(["TRACKED", "EXEMPT", "NOT_TRACKED"]),
    treatment: z.enum(["CREDIT", "INFORMATIONAL"]),
    regime: pisCofinsRegimeSchema,
    /** Base cost minus the purchase own ICMS; null when the own ICMS is not tracked. */
    base: moneySchema.nullable(),
    pis: contributionLineSchema,
    cofins: contributionLineSchema,
    pisAmount: moneySchema.nullable(),
    cofinsAmount: moneySchema.nullable(),
    total: moneySchema.nullable(),
})
    .strict();
export const purchaseLinesSchema = z
    .object({ ownIcms: purchaseOwnIcmsSchema, pisCofins: purchasePisCofinsSchema })
    .strict();
/**
 * CONFIGURED: a rule applies and its amount is in the totals. NOT_CONFIGURED:
 * no rule applies; the component is outside every total (its amount field is a
 * placeholder, not a zero cost) and does not block the fiscal result. INVALID:
 * rules exist but cannot be applied (ambiguous); the item needs review.
 */
export const costComponentStatusSchema = z.enum(["CONFIGURED", "NOT_CONFIGURED", "INVALID"]);
/** Engine 1.2.0 (costScope FISCAL): which cost components are in the totals. */
export const itemCostsSchema = z
    .object({
    operational: z.object({ status: costComponentStatusSchema }).strict(),
    commercialFee: z.object({ status: costComponentStatusSchema }).strict(),
    /** Base cost + IPI + ICMS/ICMS-ST (+ FCP and other taxed lines): valid on its own. */
    fiscalLandedUnitCost: moneySchema,
    /** Fiscal landed cost + take + logistics + freight + other; null unless both components are CONFIGURED. */
    commercialLandedUnitCost: moneySchema.nullable(),
})
    .strict();
/**
 * Engine 1.3.0 (ruleset policy `tax-family-v2`): how the ICMS-ST adjusted MVA
 * was reached. ALQ_inter is the interstate rate; ALQ_intra is the effective
 * internal burden of a destination substitute (the nominal rate only when the
 * rule states none: `effectiveInternalRateSource` and `warning` say so). The
 * ST debit always uses `destinationInternalRate`. Absent for earlier policies.
 */
export const mvaAdjustmentSchema = z
    .object({
    /** ALQ_inter. */
    interstateRate: rateSchema,
    /** ALQ_intra actually used in the adjustment (effective burden, else nominal). */
    effectiveInternalRate: rateSchema,
    /** Nominal destination internal rate: the ST debit rate, not ALQ_intra when a burden is stated. */
    destinationInternalRate: rateSchema,
    effectiveInternalRateSource: z.enum([
        "RULE",
        "NOMINAL_NOT_INFORMED",
        "NOMINAL_OUT_OF_VALIDITY",
    ]),
    originalMva: rateSchema,
    /** Full precision (up to 8 places shown); equals originalMva when not applied. */
    adjustedMva: rateSchema,
    /** True only when ALQ_intra > ALQ_inter on an interstate operation. */
    applied: z.boolean(),
    reason: z.enum(["ADJUSTED", "NOT_INTERSTATE", "INTRA_NOT_GREATER_THAN_INTER"]),
    legalReference: z.string().min(1).max(500).optional(),
    /** Set when the nominal rate stood in for a missing or expired effective burden. */
    warning: z.string().min(1).max(500).optional(),
    /**
     * Engine 1.10.0 (`icmsFcpInAlqIntra`): the destination's FECP rate added to ALQ_intra, which
     * `effectiveInternalRate` then includes.
     */
    fcpRate: rateSchema.optional(),
})
    .strict();
/** One statutory catalog entry an item's NCM falls in, with its legal basis. */
export const pisCofinsCatalogEntryRefSchema = z
    .object({
    id: z.string().min(1).max(80),
    profile: z.enum(["SINGLE_STAGE", "ZERO_RATE", "BEVERAGE"]),
    family: z.string().min(1).max(40),
    description: z.string().min(1).max(500),
    /** The legal condition the NCM cannot prove (an "Ex", a narrower description), or null. */
    condition: z.string().min(1).max(500).nullable(),
    requiresConfirmation: z.boolean(),
    law: z.string().min(1).max(120),
    article: z.string().min(1).max(60),
    provision: z.string().min(1).max(160),
    url: z.string().url(),
    validFrom: z.string().date(),
    validTo: z.string().date().nullable(),
})
    .strict();
/**
 * Engine 1.8.0 (pisCofinsProfiles): the PIS/COFINS profile every path of the item used.
 * STANDARD: the regime's rates (CST 01; credit CST 50 when non-cumulative). SINGLE_STAGE:
 * monofásico resale at zero (CST 04; no credit, CST 70). ZERO_RATE: alíquota zero (CST 06; no
 * credit, CST 73). PENDING: the catalog cannot settle it (a beverage, a condition to confirm,
 * lists that overlap, or a stated treatment the catalog does not back): no rate is assumed and the
 * item goes to review.
 */
export const pisCofinsProfileResultSchema = z
    .object({
    catalog: pisCofinsProfilesSchema,
    profile: z.enum(["STANDARD", "SINGLE_STAGE", "ZERO_RATE", "PENDING"]),
    /** CATALOG: the NCM's entry settled it; OPERATOR: the stated treatment; REGIME: no entry. */
    source: z.enum(["CATALOG", "OPERATOR", "REGIME"]),
    saleCst: z.enum(["01", "04", "06"]).nullable(),
    creditCst: z.enum(["50", "70", "73"]).nullable(),
    pendingReason: z
        .enum(["BEVERAGE_PROFILE", "REQUIRES_CONFIRMATION", "OVERLAPPING_LISTS", "NOT_IN_CATALOG"])
        .nullable(),
    /** The profile a PENDING item would take once confirmed (null when not pending). */
    candidateProfile: z.enum(["SINGLE_STAGE", "ZERO_RATE", "BEVERAGE"]).nullable(),
    entries: z.array(pisCofinsCatalogEntryRefSchema).max(4),
})
    .strict();
const cbsIbsPartSchema = z
    .object({
    /** Nominal rate of the year's published rule (fraction: 0.009 = 0.9 %). */
    rate: rateSchema,
    /** rate × (1 − reduction). */
    effectiveRate: rateSchema,
    amount: moneySchema,
    /** The published `cbsIbsRates` rule applied. */
    ruleId: z.string().uuid(),
})
    .strict();
export const cbsIbsStatusSchema = z.enum([
    "CALCULATED",
    "PENDING_RATE",
    "PENDING_CLASS",
    "PENDING_REGIME",
    "NOT_APPLICABLE",
]);
export const cbsIbsReasonSchema = z.enum([
    "BEFORE_2026", // NOT_APPLICABLE: no CBS/IBS before 2026-01-01
    "NCM_MISSING", // NOT_APPLICABLE: no valid NCM to classify
    "NO_SALE", // NOT_APPLICABLE: the item has no sale (no resale, no NET_TARGET_MARGIN / COST_PLUS_TR pricing)
    "SALE_ICMS_NOT_MODELLED", // NOT_APPLICABLE: MARKUP / TARGET_MARGIN do not calculate the sale's own ICMS
    "SALE_NOT_CALCULATED", // NOT_APPLICABLE: the sale price or the resale has no value (its own issue says why)
    "PIS_COFINS_NOT_TRACKED", // NOT_APPLICABLE: the sale PIS/COFINS are not tracked, so the base is unknown
    "CLASS_TO_CONFIRM", // PENDING_CLASS: the catalog entry depends on the description/requirement
    "CONFIRMATION_NOT_BACKED", // PENDING_CLASS: cbsIbsClass names a class no entry covering the NCM has
    "ANNEX_NOT_CATALOGUED", // PENDING_CLASS: the NCM may fall in an annex LC214_V1 does not catalogue yet
    "UNMATCHED_REVIEW", // PENDING_CLASS: no entry; platform asks review instead of 000001
    "RATE_NOT_PUBLISHED", // PENDING_RATE: the ruleset has no cbsIbsRates rule for a sphere on that date
    "DUE_REGIME_NOT_IN_ENGINE", // PENDING_REGIME: 2027+ (CBS replaces PIS/COFINS) is not calculated by 1.11.0
]);
/**
 * Engine 1.11.0 (`cbsIbs: "LC214_V1"`, ADR 0019): the CBS/IBS of the item's sale. 2026 is
 * INFORMATIONAL (LC 214 art. 348): the amounts are shown as on the NF-e, nothing is added to the
 * invoice, no credit enters the cost, PIS/COFINS stay as before. Never a silent zero: a missing rate,
 * a class to confirm or a 2027+ date is PENDING and sends the item to review.
 */
export const cbsIbsResultSchema = z
    .object({
    catalog: cbsIbsCatalogSchema,
    status: cbsIbsStatusSchema,
    reason: cbsIbsReasonSchema.nullable(),
    /** INFORMATIONAL (2026) or DUE (2027+); null before 2026. */
    mode: z.enum(["INFORMATIONAL", "DUE"]).nullable(),
    /** Year of the fiscal date (calculationDate). */
    year: z.number().int().min(2000).max(2100),
    /** Null only when the NCM is missing. */
    classification: z
        .object({
        cst: z.string().regex(/^\d{3}$/),
        cClassTrib: z.string().regex(/^\d{6}$/),
        source: z.enum(["CATALOG", "DEFAULT_STANDARD", "OPERATOR_CONFIRMED", "UNCATALOGUED"]),
        /** Share of the rate removed (fraction): "1" zero rate, "0.6" 60 % reduction, "0" none. */
        reduction: rateSchema,
        /** The NF-e pRedAliq, in percent ("60"). */
        pRedAliq: rateSchema,
        entryId: z.string().min(1).max(120).nullable(),
        annex: z.string().min(1).max(20).nullable(),
        annexItem: z.string().min(1).max(20).nullable(),
        article: z.string().min(1).max(120).nullable(),
        url: z.string().url().nullable(),
        condition: z.string().min(1).max(600).nullable(),
    })
        .strict()
        .nullable(),
    /** vBC: sale value − own ICMS − FCP − DIFAL − PIS − COFINS (LC 214 art. 12 § 2º); null when not calculated. */
    base: moneySchema.nullable(),
    baseSource: z.enum(["RESALE", "NET_TARGET_MARGIN", "COST_PLUS_TR"]).nullable(),
    cbs: cbsIbsPartSchema.nullable(),
    ibsUf: cbsIbsPartSchema.nullable(),
    ibsMun: cbsIbsPartSchema.nullable(),
    /** CBS + IBS-UF + IBS-Mun when CALCULATED, else null. */
    total: moneySchema.nullable(),
    /** What CBS/IBS add to the buyer's invoice: "0.00" in 2026 (NT 2025.002 VB01-10). */
    addedToInvoice: moneySchema,
    /** CBS/IBS credit of the purchase that lowers the cost: "0.00" in 2026. */
    purchaseCredit: moneySchema,
    /** True in 2026: PIS/COFINS are still charged in full (LC 214 art. 348 § 2º). */
    pisCofinsInForce: z.boolean(),
    /** "Informativo — compensável com PIS/COFINS (LC 214 art. 348)" in 2026, else null. */
    label: z.string().min(1).max(200).nullable(),
    legalBasis: z.array(z.string().min(1).max(300)).min(1).max(12),
})
    .strict();
export const calculationItemResultSchema = z
    .object({
    itemId: z.string(),
    position: z.number().int().nonnegative(),
    status: z.enum(["CALCULATED", "REVIEW_REQUIRED"]),
    authoritative: z.boolean(),
    monetaryBasis: monetaryBasisResultSchema.optional(),
    supplier: z
        .object({
        type: supplierTypeSchema,
        stPreviouslyPaid: z.boolean(),
        stStatus: z.enum(["PREVIOUSLY_PAID", "CURRENT_RULE", "PENDING"]),
    })
        .strict()
        .optional(),
    baseCost: z
        .object({
        amount: moneySchema,
        source: z.enum([
            "FINAL_COST",
            "ORIGINAL_COST",
            "CURRENT_SALE_PRICE",
            "SUGGESTED_SALE_PRICE",
            "ORIGINAL_SALE_PRICE",
            "MISSING",
        ]),
    })
        .strict(),
    lookupMode: z.enum(["EXACT", "NCM_UNIQUE", "MISSING", "AMBIGUOUS"]),
    rules: z
        .object({
        fiscalRuleId: z.string().uuid().nullable(),
        operationalCostRuleId: z.string().uuid().nullable(),
        commercialFeeRuleId: z.string().uuid().nullable(),
    })
        .strict(),
    rates: z
        .object({
        ipiRate: rateSchema,
        originalMva: rateSchema,
        adjustedMva: rateSchema,
        destinationInternalIcmsRate: rateSchema,
        interstateIcmsRate: rateSchema,
        takeRate: rateSchema,
    })
        .strict(),
    amounts: z
        .object({
        ipi: moneySchema,
        icmsSt: moneySchema,
        taxTotal: moneySchema,
        take: moneySchema,
        logistics: moneySchema,
        freight: moneySchema,
        other: moneySchema,
        landedUnitCost: moneySchema,
    })
        .strict(),
    financeReviewRequired: z.boolean(),
    issues: z.array(taxIssueCodeSchema),
    provenance: z.array(calculationProvenanceSchema),
    fiscalLines: fiscalLinesSchema.optional(),
    pricing: pricingResultSchema.optional(),
    acquisitionPublication: acquisitionPublicationResultSchema.optional(),
    /** Commercial scenarios only; never changes fiscal status, authority or confirmed totals. */
    acquisitionEstimate: acquisitionEstimateSchema.optional(),
    costs: itemCostsSchema.optional(),
    /** Engine 1.3.0 (tax-family-v2) only. */
    mvaAdjustment: mvaAdjustmentSchema.optional(),
    /** Engine 1.6.0 (purchaseTaxes DETAILED) only. */
    purchase: purchaseLinesSchema.optional(),
    /** Engine 1.7.0 (resale) only. */
    resale: resaleResultSchema.optional(),
    /** Engine 1.8.0 (pisCofinsProfiles) only. */
    pisCofinsProfile: pisCofinsProfileResultSchema.optional(),
    /**
     * Engine 1.7.0: the route was calculated with the "regra geral (sem ST)" because no published
     * rule states it. Not a published rule: `rules.fiscalRuleId` stays null.
     */
    generalRule: z
        .object({
        label: z.literal("regra geral (sem ST)"),
        noStBasis: icmsGeneralRuleSchema.shape.noStBasis,
        noStFactId: z.string().uuid().nullable(),
        interstateRate: rateSchema.nullable(),
        destinationInternalRate: rateSchema.nullable(),
        internalRateFactId: z.string().uuid().nullable(),
        legalBasis: icmsGeneralRuleSchema.shape.legalBasis,
    })
        .strict()
        .optional(),
    /**
     * Engine 1.9.0: the purchase route was calculated with the ST rule derived from the destination's
     * published rule (`icmsDestinationStRule`). Not a published rule for this route:
     * `rules.fiscalRuleId` stays null and the provenance is the source rule's evidence.
     */
    destinationStRule: destinationStRuleResultSchema.optional(),
    /**
     * Engine 1.10.0 (`icmsFcpInAlqIntra`): the purchase ICMS-ST into a destination whose law puts
     * FECP inside ALQ_intra. The ICMS-ST (`amounts.icmsSt`) is ST base × internal rate − own ICMS;
     * the FECP-ST is its own line, ST base × FECP rate; `stTotal` is both.
     */
    fcpInAlqIntra: fcpInAlqIntraResultSchema.optional(),
    /**
     * Engine 1.10.0 (`icmsStSenderWithholding`): who collects the purchase ICMS-ST. SENDER: the
     * supplier withholds it on its invoice as substitute under the agreement named.
     */
    stCollection: stCollectionResultSchema.optional(),
    /**
     * Engine 1.11.0 (`cbsIbs: "LC214_V1"`, ADR 0019): the CBS/IBS of the item's sale. 2026 is
     * INFORMATIONAL (LC 214 art. 348): the amounts are shown as on the NF-e, nothing is added to the
     * invoice, no credit enters the cost, PIS/COFINS stay as before. Never a silent zero: a missing rate,
     * a class to confirm or a 2027+ date is PENDING and sends the item to review.
     */
    cbsIbs: cbsIbsResultSchema.optional(),
})
    .strict();
export const batchCalculationResponseSchema = workflowContextSchema
    .extend({
    schemaVersion: z.literal("1.0.0"),
    calculationId: z.string().uuid(),
    batch: z
        .object({ index: z.number().int().nonnegative(), count: z.number().int().positive() })
        .strict(),
    idempotency: z
        .object({
        replayed: z.boolean(),
        requestHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    })
        .strict(),
    ruleset: z
        .object({
        id: z.string().uuid(),
        version: z.string().min(1).max(80),
        contentDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
        publishedAt: z.string().datetime(),
    })
        .strict(),
    engine: z
        .object({
        name: z.literal("backchannel-tax-engine"),
        version: z.string().regex(/^\d+\.\d+\.\d+$/),
        policyVersion: z.string().min(1),
        roundingMode: z.literal("HALF_UP"),
        currencyScale: z.literal(2),
    })
        .strict(),
    summary: z
        .object({
        itemCount: z.number().int().nonnegative(),
        calculatedCount: z.number().int().nonnegative(),
        reviewRequiredCount: z.number().int().nonnegative(),
        rejectedCount: z.number().int().nonnegative(),
        issueCounts: z.partialRecord(taxIssueCodeSchema, z.number().int().nonnegative()),
    })
        .strict(),
    items: z.array(calculationItemResultSchema).max(500),
    completedAt: z.string().datetime(),
})
    .strict();
/** Operator-facing lot identity on a stored calculation; never part of the engine envelope. */
export const calculationLotSchema = z
    .object({
    workbookId: z.string().uuid().optional(),
    sheetName: z.string().min(1).max(100).optional(),
    routeLabel: z.string().min(1).max(80).optional(),
    sourceSystem: z.string().trim().min(1).max(80),
    lotId: z.string().trim().min(1).max(200),
    listId: z.string().trim().min(1).max(200).optional(),
    name: z.string().trim().min(1).max(240).optional(),
})
    .strict();
export const calculationListItemSchema = z
    .object({
    calculationId: z.string().uuid(),
    completedAt: z.string().datetime(),
    workflowId: z.string().min(1),
    correlationId: z.string().min(1).max(128),
    rulesetVersion: z.string().min(1).max(80),
    rulesetDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    itemCount: z.number().int().nonnegative(),
    reviewRequiredCount: z.number().int().nonnegative(),
    rejectedCount: z.number().int().nonnegative(),
    replayed: z.boolean(),
    topIssues: z.array(taxIssueCodeSchema),
    requestedByName: z.string().min(1),
    lot: calculationLotSchema.nullable(),
})
    .strict();
const legacyArtifactRefSchema = artifactRefSchema
    .pick({
    bucket: true,
    key: true,
    step: true,
    contentType: true,
    sizeBytes: true,
    createdAt: true,
})
    .extend({ contentType: z.literal("application/json") })
    .strict();
export const dynamicTaxSnapshotV1Schema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    workflowId: z.literal("dynamic-list-upload"),
    requestId: z.string().min(1),
    correlationId: z.string().min(1),
    idempotencyKey: z.string().min(1),
    rulesetVersion: z.string().max(80).nullable(),
    rowCount: z.number().int().nonnegative(),
    reviewRequiredCount: z.number().int().nonnegative(),
    issueCounts: z.record(z.string(), z.number().int().nonnegative()),
    canonicalRef: z.object({ bucket: z.string(), key: z.string() }).strict().nullable(),
    costTaxRef: legacyArtifactRefSchema.nullable(),
    baseListRef: legacyArtifactRefSchema.nullable(),
})
    .strict();
//# sourceMappingURL=calculation.js.map